import { Sparkles, Check, Plus, ShoppingBag } from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';
import { etatEnrichissement, prochainGeste } from '../../utils/enrichissementFiche';

/**
 * Ce qui manque à ma fiche pour qu'un voisin ait envie de me parler.
 *
 * POURQUOI CET ÉCRAN, dans les mots du commanditaire :
 *
 *   « Plus on a accès aux modules Cabaza, plus la page de l'utilisateur
 *     s'enrichit. Il doit donc acheter des modules et/ou des abonnements pour
 *     que sa fiche soit de plus en plus intéressante pour le voisinage —
 *     l'effet viral et le concept de Cabaza. »
 *
 * Une fiche vide n'intéresse personne, et son auteur ne le voit pas : il la
 * lit remplie de tout ce qu'il sait déjà de lui-même. Cet encart lui montre
 * ce qu'un VISITEUR y trouve.
 *
 * IL NE S'AFFICHE QUE SUR SA PROPRE FICHE. Dire à quelqu'un ce qui manque
 * chez un autre n'a aucun sens, et signalerait à un voisin ce que la personne
 * a choisi de ne pas exposer.
 *
 * IL NE JUGE PAS. Pas de score sur cent, pas de « profil incomplet » :
 * quelqu'un qui choisit de ne rien exposer fait un choix légitime, et le lui
 * reprocher serait malvenu sur un réseau qui promet la maîtrise de ses
 * données. On montre ce qui existe, on suggère UNE chose de plus.
 */
const EnrichirMaFiche = ({ profil, modules, contenus, onAller }) => {
    const { t } = useTranslation();

    const etat = etatEnrichissement(profil, modules, contenus);
    const geste = prochainGeste(etat);

    // Rien à suggérer : la fiche est complète, ou du moins l'auteur a fait
    // tous les gestes possibles. On ne laisse pas un encart vide derrière.
    if (!geste) return null;

    const aRemplir = etat.manquants.length;
    const aAcquerir = etat.aAcquerir.length;

    return (
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-100 rounded-xl p-4 mb-4">
            <div className="flex items-start gap-3">
                <div className="bg-white/70 rounded-lg p-2 flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-cabaza-purple" />
                </div>

                <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-gray-900 text-sm">
                        {t.enrichir?.titre || 'Votre fiche vue par un voisin'}
                    </h3>

                    {/* CE QU'UN VISITEUR TROUVE DÉJÀ — avant ce qui manque.
                        Commencer par le manque donnerait le sentiment d'un
                        reproche ; commencer par l'acquis rend la suggestion
                        recevable. */}
                    <p className="text-xs text-gray-600 mt-0.5">
                        {(t.enrichir?.dejaLa || '{n} éléments déjà visibles')
                            .replace('{n}', String(etat.remplis.length))}
                    </p>

                    {/* La suggestion, une seule. */}
                    <div className="mt-3 bg-white rounded-lg p-3">
                        <div className="flex items-center gap-2 text-sm">
                            {geste.type === 'remplir' ? (
                                <Plus className="w-4 h-4 text-cabaza-purple flex-shrink-0" />
                            ) : (
                                <ShoppingBag className="w-4 h-4 text-amber-500 flex-shrink-0" />
                            )}
                            <span className="font-medium text-gray-900">{geste.libelle}</span>
                        </div>

                        <p className="text-xs text-gray-500 mt-1">
                            {geste.type === 'remplir'
                                ? (t.enrichir?.aidRemplir
                                    || "Vous avez déjà ce module : il ne reste qu'à le remplir.")
                                : (t.enrichir?.aidAcquerir
                                    || 'Ce module enrichirait votre fiche.')}
                        </p>

                        {onAller && (
                            <button
                                onClick={() => onAller(geste)}
                                className="mt-2 text-sm text-cabaza-purple hover:text-cabaza-pink font-medium"
                            >
                                {geste.type === 'remplir'
                                    ? (t.enrichir?.jyVais || "J'y vais")
                                    : (t.enrichir?.voirModules || 'Voir les modules')}
                            </button>
                        )}
                    </div>

                    {/* Le reste, en une ligne : assez pour situer l'effort qui
                        resterait, trop peu pour décourager. */}
                    {(aRemplir > 1 || aAcquerir > 0) && (
                        <p className="text-xs text-gray-500 mt-2">
                            {aRemplir > 1 && (
                                <span className="inline-flex items-center gap-1 mr-3">
                                    <Check className="w-3 h-3" />
                                    {(t.enrichir?.autresARemplir || '{n} autres à remplir')
                                        .replace('{n}', String(aRemplir - 1))}
                                </span>
                            )}
                            {aAcquerir > 0 && geste.type === 'remplir' && (
                                <span className="inline-flex items-center gap-1">
                                    <ShoppingBag className="w-3 h-3" />
                                    {(t.enrichir?.modulesDispo || '{n} modules disponibles')
                                        .replace('{n}', String(aAcquerir))}
                                </span>
                            )}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EnrichirMaFiche;
